import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import { CheckCircle, XCircle, AlertTriangle, Info, X } from 'lucide-react';

// ---------------------------------------------------------------
// Toast-Context
// ---------------------------------------------------------------
const ToastContext = createContext(null);

// ---------------------------------------------------------------
// Interne Hilfsfunktion: Liquid-Glass-Styles pro Typ
// ---------------------------------------------------------------
const getNotificationStyle = (type) => {
  switch (type) {
    case 'success':
      return {
        bgGlass: 'bg-emerald-600',
        border: 'border-emerald-500',
        shadow: 'shadow-[0_8px_32px_rgba(16,185,129,0.35)]',
        text: 'text-white',
        icon: CheckCircle,
        iconColor: 'text-white',
        glowColor: 'from-emerald-400/70 to-green-400/70'
      };
    case 'error':
      return {
        bgGlass: 'bg-red-600',
        border: 'border-red-500',
        shadow: 'shadow-[0_8px_32px_rgba(239,68,68,0.35)]',
        text: 'text-white',
        icon: XCircle,
        iconColor: 'text-white',
        glowColor: 'from-red-400/70 to-pink-400/70'
      };
    case 'warning':
      return {
        bgGlass: 'bg-amber-500',
        border: 'border-amber-400',
        shadow: 'shadow-[0_8px_32px_rgba(245,158,11,0.35)]',
        text: 'text-white',
        icon: AlertTriangle,
        iconColor: 'text-white',
        glowColor: 'from-amber-400/70 to-yellow-400/70'
      };
    default:
      return {
        bgGlass: 'bg-brand',
        border: 'border-blue-500',
        shadow: 'shadow-[0_8px_32px_rgba(37,99,235,0.35)]',
        text: 'text-white',
        icon: Info,
        iconColor: 'text-white',
        glowColor: 'from-blue-400/70 to-indigo-400/70'
      };
  }
};

// ---------------------------------------------------------------
// Einzelne Toast-Komponente (Liquid Glass Design)
// ---------------------------------------------------------------
const ToastNotification = ({ notification, onRemove }) => {
  const style = getNotificationStyle(notification.type);
  const IconComponent = style.icon;

  return (
    <div className="relative group/toast">
      {/* Liquid Glass Background */}
      <div className={`absolute inset-0 ${style.bgGlass} backdrop-blur-2xl rounded-2xl border ${style.border} ${style.shadow}`}></div>
      <div className="absolute inset-0 bg-gradient-to-t from-white/5 via-transparent to-white/10 rounded-2xl"></div>

      <div className="relative p-4 transform transition-all duration-500 ease-in-out hover:scale-[1.02] hover:shadow-[0_12px_40px_rgba(0,0,0,0.4)]">
        <div className="flex items-start space-x-3">
          {/* Liquid Glass Icon Container */}
          <div className="relative group/icon flex-shrink-0">
            <div className="absolute inset-0 bg-gradient-to-br from-white/20 via-white/10 to-transparent rounded-xl blur-sm"></div>
            <div className="relative bg-gradient-to-br from-gray-600/60 via-gray-700/50 to-gray-800/60 backdrop-blur-xl w-8 h-8 rounded-xl flex items-center justify-center shadow-[0_4px_16px_rgba(0,0,0,0.3),inset_0_1px_0_rgba(255,255,255,0.3)] border border-white/20">
              <IconComponent className={`w-4 h-4 ${style.iconColor} drop-shadow-lg`} />
              <div className="absolute top-1 left-1 w-2 h-2 bg-white/30 rounded-full blur-sm"></div>
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <p className={`text-sm font-medium ${style.text} drop-shadow-lg`}>
              {notification.message}
            </p>
          </div>

          {/* Liquid Glass Close Button */}
          <button
            onClick={() => onRemove(notification.id)}
            className="relative group/close overflow-hidden rounded-xl transition-all duration-300 transform hover:scale-[1.1] active:scale-[0.9] flex-shrink-0"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-slate-600/60 via-gray-600/60 to-slate-700/60 backdrop-blur-xl"></div>
            <div className="absolute inset-0 bg-gradient-to-t from-white/15 to-transparent"></div>
            <div className="absolute inset-0 border border-white/30 rounded-xl"></div>

            <div className="relative z-10 w-6 h-6 flex items-center justify-center text-white/80 drop-shadow-lg">
              <X className="w-3 h-3 group-hover/close:scale-110 transition-transform duration-300" />
            </div>

            <div className="absolute inset-0 rounded-xl shadow-[0_4px_16px_rgba(0,0,0,0.3)] group-hover/close:shadow-[0_8px_24px_rgba(0,0,0,0.5)] transition-all duration-300"></div>
          </button>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------
// ToastProvider – haelt State, stellt Funktionen bereit,
// rendert den fixen Container oben rechts
// ---------------------------------------------------------------
export const ToastProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);

  // Stabile Referenzen (useCallback/useMemo): Konsumenten haengen showError/showSuccess in
  // useCallback-/useEffect-Abhaengigkeiten. Ohne Stabilitaet loest jeder Toast einen Re-Render
  // des Providers aus, die Konsumenten laden neu (z. B. Feldkonfig -> Ladeanzeige), und
  // offene Dialoge darunter werden unmounted (Material-Schnellerfassung: "In SAP pruefen"
  // schloss den Transfer-Dialog samt Ergebnis, 2026-10-06).
  const showNotification = useCallback((message, type = 'info', duration = 4000) => {
    const id = Math.random().toString(36).substr(2, 9);
    const notification = { id, message, type, timestamp: Date.now() };

    setNotifications(prev => [...prev, notification]);

    // Auto-remove nach duration
    setTimeout(() => {
      setNotifications(prev => prev.filter(n => n.id !== id));
    }, duration);
  }, []);

  const removeNotification = useCallback((id) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  }, []);

  const showSuccess = useCallback((message) => showNotification(message, 'success', 3000), [showNotification]);
  const showError   = useCallback((message) => showNotification(message, 'error',   5000), [showNotification]);
  const showWarning = useCallback((message) => showNotification(message, 'warning', 4000), [showNotification]);
  const showInfo    = useCallback((message) => showNotification(message, 'info',    4000), [showNotification]);

  const value = useMemo(
    () => ({ showNotification, showSuccess, showError, showWarning, showInfo }),
    [showNotification, showSuccess, showError, showWarning, showInfo]
  );

  return (
    <ToastContext.Provider value={value}>
      {children}

      {/* Toast-Container – fixed top-right, identisch zur bisherigen Implementierung */}
      {notifications.length > 0 && (
        <div className="fixed top-4 right-4 z-[60] space-y-3 max-w-md">
          {notifications.map((notification) => (
            <ToastNotification
              key={notification.id}
              notification={notification}
              onRemove={removeNotification}
            />
          ))}
        </div>
      )}
    </ToastContext.Provider>
  );
};

// ---------------------------------------------------------------
// useToast – Hook fuer Konsumenten (derzeit: AppContent in App.js)
// Liefert: { showNotification, showSuccess, showError, showWarning, showInfo }
// ---------------------------------------------------------------
export const useToast = () => {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error('useToast muss innerhalb eines <ToastProvider> verwendet werden');
  }
  return ctx;
};

export default ToastProvider;
